import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GeoAddress } from '../models/common/geo-address.model';
import { City } from '../models/common/city.model';
import { Location } from './location.model';
import { SessionStorageService } from '../services/session-storage.service';
import { LocationDirectoryOption, LocationService } from './location.service';
import { BaseMasterDataService } from '../services/base-master-data.service';
import { TransportPlanningService } from '../transport-planning/transport-planning.service';
import { LocationZone } from '../transport-planning/transport-planning.model';
import { MenuItem, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { catchError, EMPTY, expand, forkJoin, of, reduce } from 'rxjs';
import * as XLSX from 'xlsx';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ChipModule } from 'primeng/chip';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';

interface LocationSearchForm {
    code: FormControl<string | null>;
    name: FormControl<string | null>;
    address: FormControl<string | null>;
    company: FormControl<string | null>;
    type: FormControl<string | null>;
    contact: FormControl<string | null>;
    countryId: FormControl<number | null>;
    cityId: FormControl<number | null>;
    district: FormControl<string | null>;
    retailerId: FormControl<number | null>;
    locationZoneId: FormControl<number | null>;
}

type LocationFilters = ReturnType<FormGroup<LocationSearchForm>['getRawValue']>;
type NumberOption = { label: string; value: number };
type CityOption = NumberOption & { countryId: number | null };

@Component({
    selector: 'app-location',
    templateUrl: './location.component.html',
    styleUrls: ['./location.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        BreadcrumbModule,
        InputTextModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        SelectModule,
        TagModule,
        TooltipModule,
        ChipModule
    ], 
    providers: [

      MessageService,
      LocationService,
      SessionStorageService
    ]
})
export class LocationComponent implements OnInit {

    canViewLocations = false;
    canCreateLocation = false;
    canEditLocation = false;
    canDeleteLocation = false;

    locations: Location[];
    filteredLocations: Location[] = [];
    first = 0;
    rows = 20;
    totalRequests = 0;
    totalMatching = 0;
    warehouseCount = 0;
    customerCount = 0;
    isLoading = true;
    isExporting = false;
    isReferenceLoading = false;
    lastUpdated: Date | null = null;
    countryOptions: NumberOption[] = [];
    cityOptions: CityOption[] = [];
    retailerOptions: NumberOption[] = [];
    zoneOptions: NumberOption[] = [];
    private cityNames = new Map<number, string>();
    private zoneNames = new Map<number, string>();
    private zoneCodes = new Map<number, string>();
    private countryNames = new Map<number, string>();
    private directoryOptions: LocationDirectoryOption[] = [];
    private appliedFilters: LocationFilters = this.emptyFilters();
    private loadSequence = 0;

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;
    locationTypeOptions = [
        { label: 'Customer Site', value: 'CUSTOMER' },
        { label: 'Warehouse', value: 'WAREHOUSE' },
        { label: 'Distribution Center', value: 'DISTRIBUTION_CENTER' },
        { label: 'Cross Dock', value: 'CROSS_DOCK' },
        { label: 'POSM Store', value: 'POSM_STORE' },
        { label: 'Retail Store', value: 'STORE' },
        { label: 'Office', value: 'OFFICE' },
        { label: 'Branch', value: 'BRANCH' },
        { label: 'Depot', value: 'DEPOT' },
        { label: 'Yard', value: 'YARD' },
        { label: 'Hub', value: 'HUB' },
        { label: 'Factory / Plant', value: 'FACTORY' },
        { label: 'Supplier / Vendor Site', value: 'SUPPLIER' },
        { label: 'Port / Terminal', value: 'PORT' },
        { label: 'Service Center', value: 'SERVICE_CENTER' },
        { label: 'Temporary Site', value: 'TEMPORARY_SITE' },
        { label: 'Carrier', value: 'CARRIER' }
    ];

    searchForm = new FormGroup<LocationSearchForm>({
        code: new FormControl(''),
        name: new FormControl(''),
        address: new FormControl(''),
        company: new FormControl(''),
        type: new FormControl(null),
        contact: new FormControl(''),
        countryId: new FormControl(null),
        cityId: new FormControl(null),
        district: new FormControl(''),
        retailerId: new FormControl(null),
        locationZoneId: new FormControl(null)
    });

    constructor(private route: ActivatedRoute,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private locationService: LocationService,
        private baseMasterDataService: BaseMasterDataService,
        private transportPlanningService: TransportPlanningService,
        private messageService: MessageService,
        private permissionService: PermissionService) {
        this.locations = [];
    }

    addLocation() {
        if (!this.canCreateLocation) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to create locations.' });
            return;
        }
        this.router.navigate(['/location/create']);
    }

    viewLocation(location: Location) {
        this.router.navigate(['/location/view', location.id]);
    }

    editLocation(location: Location) {
        if (!this.canEditLocation) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to edit locations.' });
            return;
        }
        this.router.navigate(['/location/edit', location.id]);
    }

    deleteLocation(location: Location) {
        if (!this.canDeleteLocation) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to delete locations.' });
            return;
        }
        throw new Error('Method not implemented.');
    }


    ngOnInit(): void {
        this.canViewLocations = this.permissionService.hasPermission(app_permissions.location.records.view);
        this.canCreateLocation = this.permissionService.hasPermission(app_permissions.location.records.create);
        this.canEditLocation = this.permissionService.hasPermission(app_permissions.location.records.edit);
        this.canDeleteLocation = this.permissionService.hasPermission(app_permissions.location.records.delete);
        this.breadcrumbItems = [{ label: 'Locations', routerLink: '/location' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.searchForm.controls.countryId.valueChanges.subscribe(countryId => {
            const cityId = this.searchForm.controls.cityId.value;
            if (cityId != null && !this.cityOptions.some(city => city.value === cityId && (countryId == null || city.countryId === countryId))) {
                this.searchForm.controls.cityId.setValue(null);
            }
        });
        this.loadDirectoryOptions();
        this.loadLocations();
    }

    loadLocations() {
        if (!this.canViewLocations) {
            this.isLoading = false;
            return;
        }
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        const loadSequence = ++this.loadSequence;
        this.locationService.getDirectory(companyId, Math.floor(this.first / this.rows), this.rows, this.appliedFilters).subscribe({
            next: response => {
                if (loadSequence !== this.loadSequence) return;
                this.locations = response.items || [];
                this.filteredLocations = this.locations;
                this.totalMatching = response.count || 0;
                this.lastUpdated = new Date();
                this.isLoading = false;
            },
            error: () => {
                if (loadSequence !== this.loadSequence) return;
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Locations', detail: 'Unable to load locations.' });
            }
        });
    }

    refresh() {
        this.loadDirectoryOptions();
        this.loadLocations();
    }

    applySearch(resetPage = true) {
        this.appliedFilters = { ...this.searchForm.getRawValue() };
        if (resetPage) this.first = 0;
        this.loadLocations();
    }

    onPage(event: { first?: number; rows?: number }) {
        const first = event.first ?? 0;
        const rows = event.rows ?? this.rows;
        if (first === this.first && rows === this.rows) return;
        this.first = first;
        this.rows = rows;
        this.loadLocations();
    }

    clearSearch() {
        this.searchForm.reset({
            code: '',
            name: '',
            address: '',
            company: '',
            type: null,
            contact: '',
            countryId: null,
            cityId: null,
            district: '',
            retailerId: null,
            locationZoneId: null
        });
        this.applySearch();
    }

    getLocationAddress(location: Location): string {
        if (!Array.isArray(location?.address?.addressElements))
            return location.addressText || '-';
        const geoAddress: GeoAddress = new GeoAddress().getGeoAddressFromObject(location.address);
        return [geoAddress.getSubLocality(), geoAddress.getLocality()].filter(Boolean).join(', ') || '-';
    }

    locationCode(location: Location): string {
        return location.externalLocationReference || 'LOC-' + location.id;
    }

    locationContactSearchText(location: Location): string {
        const contacts = [location.defaultContact, ...(location.contacts || [])].filter(contact => !!contact);
        return contacts
            .flatMap(contact => [contact.name, contact.phoneNumber, contact.emailAddress])
            .filter(Boolean)
            .join(' ');
    }

    openMap(location: Location) {
        if (!location.location?.latitude || !location.location?.longitude)
            return;
        window.open(`https://maps.google.com/?q=${location.location.latitude},${location.location.longitude}`, '_blank');
    }

    get activeSearchCount(): number {
        return Object.values(this.appliedFilters).filter(value => value != null && String(value).trim() !== '').length;
    }

    get availableCityOptions(): CityOption[] {
        const countryId = this.searchForm.controls.countryId.value;
        return countryId == null ? this.cityOptions : this.cityOptions.filter(city => city.countryId === countryId);
    }

    countryName(location: Location): string {
        return location.country?.name || (this.countryId(location) != null ? `Country #${this.countryId(location)}` : '-');
    }

    cityName(location: Location): string {
        return location.cityId == null ? '-' : this.cityNames.get(location.cityId) || `City #${location.cityId}`;
    }

    districtLabel(location: Location): string {
        if (location.district?.trim()) return location.district.trim();
        return Array.isArray(location.address?.addressElements)
            ? new GeoAddress().getGeoAddressFromObject(location.address).getSubLocality() || '-'
            : '-';
    }

    retailerName(location: Location): string {
        return location.retailer?.name || (location.retailer?.id != null ? `Retailer #${location.retailer.id}` : '-');
    }

    zoneName(location: Location): string {
        return location.locationZoneId == null ? '-' : this.zoneNames.get(location.locationZoneId) || `Zone #${location.locationZoneId}`;
    }

    exportToExcel(): void {
        if (!this.canViewLocations || this.isLoading || this.isReferenceLoading || this.isExporting || !this.totalMatching) return;
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        const filters = { ...this.appliedFilters };
        this.isExporting = true;
        this.locationService.getDirectory(companyId, 0, 100, filters).pipe(
            expand(result => result.hasMore
                ? this.locationService.getDirectory(companyId, Number(result.offset) + 1, 100, filters) : EMPTY),
            reduce((all, result) => all.concat(result.items || []), [] as Location[])
        ).subscribe({
            next: locations => {
                XLSX.writeFile(this.buildExportWorkbook(locations), `locations_${new Date().toISOString().slice(0, 10)}.xlsx`);
                this.isExporting = false;
            },
            error: () => {
                this.isExporting = false;
                this.messageService.add({ severity: 'error', summary: 'Locations', detail: 'Unable to export locations.' });
            }
        });
    }

    buildExportWorkbook(locations: Location[] = this.filteredLocations): XLSX.WorkBook {
        const headers = ['Code', 'Name', 'Type', 'Address', 'Country Code', 'Country', 'City', 'District',
            'Company', 'Retailer / Store Chain', 'Zone Code', 'Location Zone', 'Latitude', 'Longitude',
            'Short Description', 'Long Description', 'Telephone Number', 'Default Contact',
            'Default Contact Phone', 'Default Contact Email', 'Contacts With Access (Info)',
            'Merchandiser Count (Info)'];
        const rows = locations.map(location => [
            location.externalLocationReference || '', location.name || '', location.locationType || '',
            location.addressText || '', location.country?.countryCode || '', location.country?.name || '',
            location.cityId == null ? '' : this.cityNames.get(location.cityId) || '',
            location.district || '', location.company?.name || '', location.retailer?.name || '',
            location.locationZoneId == null ? '' : this.zoneCodes.get(location.locationZoneId) || '',
            location.locationZoneId == null ? '' : this.zoneNames.get(location.locationZoneId) || '',
            location.location?.latitude ?? '', location.location?.longitude ?? '',
            location.shortDescription || '', location.longDescription || '', location.telephoneNumber || '',
            location.defaultContact?.name || '',
            location.defaultContact?.phoneNumber || '', location.defaultContact?.emailAddress || '',
            (location.contacts || []).map(contact => contact.name).filter(Boolean).join(', '),
            location.merchandisers?.length || 0
        ]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([headers, ...rows]), 'Locations');
        const instructions = [
            ['Import instructions'],
            ['Code matches a Location in this company. Leave it blank to create a new Location.'],
            ['New rows require Name, Type, Country Code or Country, City, Latitude and Longitude.'],
            ['Blank optional cells preserve existing values on updates.'],
            ['Contacts With Access (Info) and Merchandiser Count (Info) are informational and ignored on upload.']
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(instructions), 'Instructions');
        return workbook;
    }

    private countryId(location: Location): number | null {
        return location.countryId ?? location.country?.id ?? null;
    }

    private emptyFilters(): LocationFilters {
        return { code: '', name: '', address: '', company: '', type: null, contact: '',
            countryId: null, cityId: null, district: '', retailerId: null, locationZoneId: null };
    }

    private buildFilterOptions(): void {
        const countries = new Map<number, string>();
        const cities = new Map<number, CityOption>();
        const retailers = new Map<number, string>();
        const zones = new Map<number, string>();
        for (const option of this.directoryOptions) {
            const countryId = option.countryId;
            if (countryId != null) countries.set(countryId, this.countryNames.get(countryId) || `Country #${countryId}`);
            if (option.cityId != null) cities.set(option.cityId, {
                value: option.cityId, countryId, label: this.cityNames.get(option.cityId) || `City #${option.cityId}`
            });
            if (option.retailerId != null) retailers.set(option.retailerId, option.retailerName || `Retailer #${option.retailerId}`);
            if (option.zoneId != null) zones.set(option.zoneId, this.zoneNames.get(option.zoneId) || `Zone #${option.zoneId}`);
        }
        const sort = (a: NumberOption, b: NumberOption) => a.label.localeCompare(b.label);
        this.countryOptions = [...countries].map(([value, label]) => ({ value, label })).sort(sort);
        this.cityOptions = [...cities.values()].sort(sort);
        this.retailerOptions = [...retailers].map(([value, label]) => ({ value, label })).sort(sort);
        this.zoneOptions = [...zones].map(([value, label]) => ({ value, label })).sort(sort);
    }

    private loadDirectoryOptions(): void {
        if (!this.canViewLocations) return;
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.isReferenceLoading = true;
        this.locationService.getDirectoryOptions(companyId).subscribe({
            next: result => {
                this.directoryOptions = result.options || [];
                this.totalRequests = result.total;
                this.warehouseCount = result.warehouses;
                this.customerCount = result.customers;
                this.buildFilterOptions();
                this.loadReferenceData(companyId);
            },
            error: () => {
                this.isReferenceLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Locations', detail: 'Unable to load location filters.' });
            }
        });
    }

    private loadReferenceData(companyId: number): void {
        const countryIds = [...new Set(this.directoryOptions.filter(option => option.cityId != null)
            .map(option => option.countryId).filter((id): id is number => id != null))];
        forkJoin({
            countries: this.baseMasterDataService.getCountries('en-US').pipe(catchError(() => of([]))),
            cities: countryIds.length ? forkJoin(countryIds.map(countryId =>
                this.baseMasterDataService.getCities('en-US', countryId).pipe(catchError(() => of([] as City[]))))) : of([] as City[][]),
            zones: companyId ? this.transportPlanningService.getLocationZones(companyId, false)
                .pipe(catchError(() => of([] as LocationZone[]))) : of([] as LocationZone[])
        }).subscribe(({ countries, cities, zones }) => {
            this.countryNames = new Map(countries.map(country => [country.id, country.name]));
            this.cityNames = new Map(cities.flat().map(city => [city.id, city.cityName]));
            this.zoneNames = new Map(zones.filter(zone => zone.id != null).map(zone => [zone.id!, zone.name]));
            this.zoneCodes = new Map(zones.filter(zone => zone.id != null).map(zone => [zone.id!, zone.code]));
            this.buildFilterOptions();
            this.isReferenceLoading = false;
        });
    }

}
